import { useMemo } from "react";
import { useSelector } from "react-redux";

import useLocationHolidays from "../../../../../holidays/controller/use-location-holidays";
import { Holiday } from "../../../../../holidays/types";
import { selectEffectiveWeekStartDay } from "../../../../store/scheduler-slice";
import { getCopyTargetStart, getDayNumber } from "../utils";

interface CopiedShift {
	id: number;
	startDateTime: string;
	pasteDateStart?: string | Date;
	locationId?: number;
	locationName?: string;
}

export interface CopyShiftHoliday {
	dateKey: string;
	holidays: Holiday[];
	locationName: string;
}

/**
 * Which copied shifts land on a holiday at their Location once re-based onto
 * the paste (target) week, keyed by source shift id.
 */
const useCopyShiftHolidays = (shifts: CopiedShift[]) => {
	const weekStartDay = useSelector(selectEffectiveWeekStartDay);
	const locationIds = useMemo(
		() => shifts.map((shift) => Number(shift.locationId)).filter(Boolean),
		[shifts]
	);
	const { getHolidaysAtInstant, getLocation } = useLocationHolidays(locationIds);

	return useMemo(() => {
		const byShiftId = new Map<number, CopyShiftHoliday>();
		const weekStartsOn = getDayNumber(weekStartDay);
		shifts.forEach((shift) => {
			if (!shift.locationId || !shift.pasteDateStart) return;
			const targetStart = getCopyTargetStart(
				shift.startDateTime,
				shift.pasteDateStart,
				weekStartsOn
			);
			const { dateKey, holidays } = getHolidaysAtInstant(
				shift.locationId,
				targetStart
			);
			if (holidays.length === 0) return;
			byShiftId.set(shift.id, {
				dateKey,
				holidays,
				locationName:
					shift.locationName ?? getLocation(shift.locationId)?.name ?? "",
			});
		});
		return byShiftId;
	}, [shifts, weekStartDay, getHolidaysAtInstant, getLocation]);
};

export default useCopyShiftHolidays;
